import React, { useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap-5';

import { STATES } from 'components/state_select';
import Controls from './controls';
import pluralize from 'pluralize';
import Select from 'react-select';
import { map } from 'lodash';
import type { Profile, Region } from 'bundles/connect/types';

type Props = {
  counties: { [key: string]: string[]};
  profile: Profile;
  saving: boolean;
  onBack: () => void;
  onSave: () => void;
  onValueChange: (id: number, key: string, val?: string | number) => void;
}

type SelectOption = {
  label: string;
  value: string | number
};


export default function CountySelect({ counties, profile, saving, onBack, onSave, onValueChange }: Props) {
  const [unselectedCounties, setUnselectedCounties] = useState<{ [state: string]: string[] }>(counties);

  const submit = (e) => {
    e.preventDefault();
    onSave();
  };

  const isDisabled = () => {
    return profile.regions.some(region => !region.state || !region.county) || saving;
  };

  const handleStateChange = (state, regionId) => {
    onValueChange(regionId, 'state', state);
    onValueChange(regionId, 'county', undefined);
  };

  const handleCountySelect = (option: SelectOption, region: Region) => {
    setUnselectedCounties((prev) => {
      const newCounties = { ...prev };
      const state = region.state;
      const prevCounty = region.county;

      const stateCounties = [...(newCounties[state] || [])];
      const newIndex = stateCounties.indexOf(String(option.value));
      if (newIndex > -1) {
        stateCounties.splice(newIndex, 1);
      }

      if (prevCounty && !stateCounties.includes(prevCounty)) {
        stateCounties.push(prevCounty);
        stateCounties.sort();
      }

      newCounties[state] = stateCounties;
      return newCounties;
    });
    onValueChange(region.id, 'county', option.value);
  };

  const stateOptions = useMemo(() => map(STATES, (k, v) => ({ label: `${v} - ${k}`, value: v })), []);

  return (
    <>
      <Form onSubmit={submit}>
        <Row className="gap-3 flex-column">
          <Col>
            <Row>
              <Col className="fw-bolder">
                {profile.regions.length === 1 ? 'Residential' : 'Regional'} Plan
              </Col>
              <Col className="text-end">
                {profile.regions.length} {pluralize('County', profile.regions.length)} remaining
              </Col>
            </Row>
            <small className="text-muted">Your Selected Plan</small>
          </Col>
          {profile.regions.map(region => (
            <Col key={region.id}>
              <Row>
                <Col xs={6}>
                  <Form.Label>State *</Form.Label>
                  <Select
                    options={stateOptions}
                    value={region.state ? { value: region.state, label: STATES[region.state] } : null}
                    onChange={(option: SelectOption) => {
                      handleStateChange(option.value, region.id);
                    }}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label>County *</Form.Label>
                  <Select
                    options={(unselectedCounties[region.state] || []).map(county => ({ value: county, label: county }))}
                    value={region.county ? { value: region.county, label: region.county } : null}
                    onChange={(option: SelectOption) => {
                      handleCountySelect(option, region);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          ))}
          <Col>
            <Controls
              disabled={isDisabled()}
              saving={saving}
              showSave
              onBack={onBack}
            />
          </Col>
        </Row>
      </Form>
    </>
  );
}
